import React, { useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import { isDarkTheme } from '@/constants/Colors';
import { landingPalette } from '@/components/Landing/landingTokens';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import LandingEditorialHero from '@/components/Landing/LandingEditorialHero';
import LandingWorkMode from '@/components/Landing/LandingWorkMode';
import LandingMac from '@/components/Landing/LandingMac';
import LandingWorkAgent from '@/components/Landing/LandingWorkAgent';
import LandingBots from '@/components/Landing/LandingBots';
import LandingWorkAreas from '@/components/Landing/LandingWorkAreas';
import LandingValueStory, { LandingChatSection } from '@/components/Landing/LandingValueStory';
import LandingPersonaLinks from '@/components/Landing/LandingPersonaLinks';
import LandingClosing from '@/components/Landing/LandingClosing';
import PricingComparison from '@/components/Pricing/PricingComparison';
import LivingMoment from '@/components/Landing/LivingMoment';
import PublicFooter from '@/components/PublicFooter';
import { GAB_AI_SOFTWARE_ID } from '@/constants/config';

export default function LandingPage() {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();

  useLandingStyles();

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: landingPalette(isDark).page,
  }), [isDark]);

  return (
    <View style={containerStyle}>
      <MetaHead
        title="AI That Works | Gab AI"
        description="Get more done, faster. Hand off research, videos, websites, documents, and recurring tasks to your AI employees and Gab AI Work Agent. Make more time for living."
        canonical="/"
        keywords="AI image generator, AI video generator, AI music generator, AI writing, AI code assistant, AI voice chat, AI file analysis, ChatGPT alternative, private AI, Gab AI, all-in-one AI"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Gab AI - AI That Works',
          description: 'Put AI employees to work on research, videos, websites, documents, and scheduled tasks. Get more done with less effort and make more time for life.',
          url: 'https://gab.ai',
          mainEntity: {
            // Same node as the site-wide one in `app/+html.jsx`, referenced by
            // @id so the two merge rather than competing.
            '@type': 'SoftwareApplication',
            '@id': GAB_AI_SOFTWARE_ID,
            name: 'Gab AI',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web, iOS, Android, macOS',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
            },
            featureList: [
              'Gab AI Work Agent: AI Workspace with Its Own Computer',
              'Access to 100+ Leading AI Models',
              'Automatic Model Routing',
              'AI Image Generation',
              'AI Video Generation',
              'AI Music Generation',
              'AI 3D Model Generation',
              'AI Writing Assistant',
              'AI Code Assistant',
              'Playable HTML Game Creation',
              'AI Video Production with Music and Voiceover',
              'Word, PDF, PowerPoint, and Excel File Creation',
              'AI Voice Chat',
              'AI File Analysis',
              'Web Search',
              'Image Editing',
              'AI Memory',
              'Custom AI Bots and Scheduled Tasks',
            ],
          },
        }}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole="main"
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <LandingEditorialHero topInset={insets.top} />
        <PricingComparison />
        <LandingWorkMode />
        <LandingMac />
        <LandingChatSection />
        <LandingBots />
        <LivingMoment scene="family" />
        <LandingWorkAreas />
        <LivingMoment scene="picnic" />
        <LandingWorkAgent />
        <LivingMoment scene="garden" />
        <LandingValueStory />
        <LandingPersonaLinks />
        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
